import { http } from "@client/shared/services";
import { jwksApi, getAccountJwksToken } from "@client/shared/services/api-communicator";
import { responseFormatter } from "@client/utils";
import { noop } from "@client/shared/constants";
import config from "@client/config";

const RC_PRODUCTION_URL = config.rcApiBaseUrl.includes("devtest")
  ? "https://platform.ringcentral.com/restapi/v1.0"
  : config.rcApiBaseUrl;

const getVoicemail = async (
  libraryItemId,
  accountUid = "~",
  query = "",
  rateLimitReachedCallback = noop
) => {
  const url = `${RC_PRODUCTION_URL}/account/${accountUid}/voicemail-library/${libraryItemId}?perPage=1000`;
  return await responseFormatter.rc(jwksApi.get(`${url}${query}`), {
    hooks: { rateLimitReached: rateLimitReachedCallback },
  });
};

const addVoicemail = async (data, accountUid = "~") => {
  const url = `${RC_PRODUCTION_URL}/account/${accountUid}/voicemail-library`;
  return await responseFormatter.rc(jwksApi.post(url, data, {
    headers: {
      'Content-Disposition': 'form-data'
    }
  }));
};

const addVoicemailInternal = (payload) => {
  return responseFormatter(
    http.post(`/upload-file`, payload, {
      setAuthHeader: true,
    })
  );
};

const deleteVoicemail = async (libraryId, accountUid = "~") => {
  const url = `${RC_PRODUCTION_URL}/account/${accountUid}/voicemail-library/${libraryId}`;
  return await responseFormatter.rc(jwksApi.delete(url, {
    headers: {
      'Content-Disposition': 'form-data'
    }
  }));
};

const createBroadcast = (payload) => {
  return responseFormatter(
    http.post(`/broadcast`, payload, {
      setAuthHeader: true,
    })
  );
};

const updateBroadcast = (payload, id) => {
  return responseFormatter(
    http.put(`/broadcast/${id}`, payload, {
      setAuthHeader: true,
    })
  );
};

const getBroadcasts = (query = "") => {
  return responseFormatter(
    http.get(`/broadcast${query}`, {
      setAuthHeader: true,
    })
  );
};

const deleteBroadcast = (query = "") => {
  return responseFormatter(
    http.delete(`/broadcast${query}`, {
      setAuthHeader: true,
    })
  );
};
const setUserPreference = (id, payload = {}) => {
  return responseFormatter(
    http.patch(`/user/${id}`, payload, {
      setAuthHeader: true,
    })
  );
};

const makeRingoutCall = async (fromNumber, toNumber = config.voicemailNumber, accountUid = "~", extensionId = "~") => {
  const payload = {
    "from": {"phoneNumber": fromNumber},
    "to": {"phoneNumber": toNumber},
  };
  const url = `${RC_PRODUCTION_URL}/account/${accountUid}/extension/${extensionId}/ring-out`;
  return await responseFormatter.rc(jwksApi.post(url, payload));
};

const cancelRingout = async (ringoutId, accountUid = "~", extensionId = "~") => {
  const url = `${RC_PRODUCTION_URL}/account/${accountUid}/extension/${extensionId}/ring-out/${ringoutId}`;
  return await responseFormatter.rc(jwksApi.delete(url));
};

const getRingoutCallStatus = async (rignoutId, accountUid = "~", extensionId = "~") => {
  const url = `${RC_PRODUCTION_URL}/account/${accountUid}/extension/${extensionId}/ring-out/${rignoutId}`;
  return await responseFormatter.rc(jwksApi.get(url));
};

const getMessageStore = async (accountUid = "~", extensionId = "~", query = "", rateLimitReachedCallback = noop) => {
  const { data: token, error } = await getAccountJwksToken(accountUid);
  const url = `${RC_PRODUCTION_URL}/account/${accountUid}/extension/${extensionId}/message-store?perPage=1000`;
  return new Promise(async resolve => {
    const messages = [];
    let pageCounter = 1;
    const { data, error, paging } = await responseFormatter.rc(
      jwksApi.get(`${url}${query}`,
      {
        hasToken: token,
        accountUid,
        hooks: { rateLimitReached: rateLimitReachedCallback },
        headers: {
          Authorization: `Bearer ${token && token.accessToken}`,
        },
      })
    );
    if (error) {
      resolve({ data: null, error });
    } else {
      messages.push(...data);
      if (paging.totalPages > 1) {
        while (pageCounter <= paging.totalPages) {
          pageCounter += 1;
          const { data, error } = await responseFormatter.rc(
            jwksApi.get(`${url}&page=${pageCounter}${query}`, {
              hasToken: token,
              accountUid,
              hooks: { rateLimitReached: rateLimitReachedCallback },
              headers: {
                Authorization: `Bearer ${token && token.accessToken}`,
              },
            }));
          error ? resolve({ data: null, error }) : messages.push(...data);
        }
      }
      resolve({ data: messages, error: null });
    }
  });
};

const getMessageContent = async (messageId, attachmentId, accountUid = "~", extensionId = "~") => {
  const { data: token, error } = await getAccountJwksToken(accountUid);
  const url = `${RC_PRODUCTION_URL}/account/${accountUid}/extension/${extensionId}/message-store/${messageId}/content/${attachmentId}`;
  return await responseFormatter.rc(
    jwksApi.get(url, {
      hasToken: token,
      accountUid,
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token && token.accessToken}`,
      },
    }) 
  );
};

const parsePhoneNumber = data => {
  const url = `${RC_PRODUCTION_URL}/number-parser/parse`;
  return responseFormatter.rc(jwksApi.post(url, data));
};

const uploadVoicemail = async (payload = {}) => {
  return await responseFormatter(
    http.post(`/upload-voicemail`, payload, {
      setAuthHeader: true
    })
  );
};

const getTimestamp = () => {
  return responseFormatter(
    http.get(`/timestamp`, {
      setAuthHeader: true,
    })
  );
};

const DashoardService = {
  getVoicemail,
  createBroadcast,
  getBroadcasts,
  deleteBroadcast,
  addVoicemail,
  updateBroadcast,
  setUserPreference,
  makeRingoutCall,
  getRingoutCallStatus,
  getMessageStore,
  getMessageContent,
  cancelRingout,
  deleteVoicemail,
  parsePhoneNumber,
  uploadVoicemail,
  getTimestamp,
  addVoicemailInternal
};

export default DashoardService;
